import { HttpErrorResponse } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { CategorySelection, TaskTypeAPIModel } from 'src/app/models/api.model'
import { ActivityService } from 'src/app/services/activity/activity.service'
import { CommonService, addDaysToUIFormattedDate } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { addDisabledFormControl, removeFormControl } from 'src/app/utils/form-control.util'

@Component({
  selector: 'app-activity',
  templateUrl: './create-activity.component.html',
  styleUrls: ['./create-activity.component.scss']
})
export class CreateActivityComponent {
  createActivityForm: FormGroup
  filesToBeUploaded: FileList

  activityTaskTypes: TaskTypeAPIModel[] = []
  activityTypes = []
  uploadedDocuments: any[]

  readonly typeControlName = 'type'
  private readonly nameControlName = 'name'
  private readonly TASK_CATEGORY = 'taskCategory'
  readonly TASK_SUB_CATEGORY = 'taskSubCategory'
  readonly RESPONSE_DUE_DATE_TIME_CONTROL_NAME = 'responseDueDatetime'

  readonly module = GlobalConstants.MODULE.ACTIVITY_TRACKER
  selectTaskTypesSubscription$: any
  addResponseDueByToForm = addDisabledFormControl(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME, '', [])
  removeResponseDueByToForm = removeFormControl(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME)

  constructor(
    private formBuilder: FormBuilder,
    private activityService: ActivityService,
    private cs: CommonService,
    private documentService: DocumentService,
    private initializeService: InitializeService
  ) {}

  ngOnInit(): void {
    this.buildCreateActivityForm()
  }

  buildCreateActivityForm() {
    this.createActivityForm = this.formBuilder.group({
      [this.nameControlName]: ['', [Validators.required]],
      [this.typeControlName]: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5000)]],
      addToCalendar: [false, [Validators.required]],
      notify: [false, []],
      [this.TASK_CATEGORY]: ['', [Validators.required]],
      [this.TASK_SUB_CATEGORY]: ['', [Validators.required]]
    })
  }

  saveAndExit() {
    if (this.createActivityForm.invalid) {
      this.createActivityForm.markAllAsTouched()
      return
    }
    const selectedTaskType = this.activityTaskTypes.find(
      (taskType) => taskType.code === this.createActivityForm.value[this.nameControlName]
    )
    const responseDueInDays = selectedTaskType.responseDueInDays == -1 ? 7 : selectedTaskType.responseDueInDays
    const status = selectedTaskType.statusList.find((status) => status.defaultStatus).code

    const activity = {
      ...this.createActivityForm.getRawValue(),
      documents: this.uploadedDocuments,
      responseDueInDays,
      status,
      taskCode: selectedTaskType.code,
      name: selectedTaskType.label
    }
    delete activity.responseDueDatetime
    this.activityService.dispatchSaveActivityAction(
      activity,
      this.filesToBeUploaded,
      this.cs.createRoutingURL([GlobalConstants.ROUTING.ACTIVITY, GlobalConstants.ROUTING.DASHBOARD])
    )
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.ACTIVITY, GlobalConstants.ROUTING.DASHBOARD)
  }

  upload() {
    this.documentService
      .uploadDocuments(
        GlobalConstants.MODULE.ACTIVITY_TRACKER,
        GlobalConstants.DOCUMENT_TYPE.ACTIVITY_DOC,
        this.filesToBeUploaded
      )
      .subscribe({
        next: (responses) => {
          this.uploadedDocuments = responses
        },
        error: (error: HttpErrorResponse) => {
          this.cs.showError(error.message)
        },
        complete: () => {
          this.filesToBeUploaded = null
          this.cs.showSuccess('Successfully Uploaded')
        }
      })
  }

  handleFileInput(files: FileList) {
    this.filesToBeUploaded = files
  }

  get f() {
    return this.createActivityForm.controls
  }

  private getActivityNameAndType(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection

    this.selectTaskTypesSubscription$ = this.initializeService
      .getFilteredTaskTypes(GlobalConstants.MODULE.ACTIVITY_TRACKER, selectedCategory, selectedSubCategory)
      .subscribe((taskTypes: TaskTypeAPIModel[]) => {
        if (taskTypes) {
          this.activityTaskTypes = taskTypes
        }
      })
  }

  onActivityNameSelectChange(selectedValue: any) {
    const selectedTaskType = this.activityTaskTypes.find((taskType) => taskType.code === selectedValue)
    if (selectedTaskType.offlineTask && selectedTaskType.onlineTask) {
      this.setActivityTypesAndEnableControl([GlobalConstants.TASK_TYPE.ONLINE, GlobalConstants.TASK_TYPE.OFFLINE])
    } else if (selectedTaskType.onlineTask) {
      this.setActivityTypesAndDisableControl(GlobalConstants.TASK_TYPE.ONLINE)
    } else if (selectedTaskType.offlineTask) {
      this.setActivityTypesAndDisableControl(GlobalConstants.TASK_TYPE.OFFLINE)
    }
    if (selectedTaskType.responseDueInDays == -1) {
      this.addResponseDueByToForm(this.createActivityForm)
      this.createActivityForm.get(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME).setValue(addDaysToUIFormattedDate(7))
    } else {
      this.removeResponseDueByToForm(this.createActivityForm)
    }
  }

  private setActivityTypesAndEnableControl(types: string[]) {
    this.activityTypes = types
    this.enableActivityTypeControl()
  }

  private setActivityTypesAndDisableControl(type: string) {
    this.activityTypes = [type]
    this.disableActivityTypeControl(type)
  }

  private enableActivityTypeControl() {
    this.createActivityForm.controls[this.typeControlName].enable()
  }

  private disableActivityTypeControl(type: string) {
    this.createActivityForm.controls[this.typeControlName].setValue(type)
    this.createActivityForm.controls[this.typeControlName].disable()
  }

  onCategorySelected(selectedCategorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = selectedCategorySelection

    this.createActivityForm.get(this.TASK_CATEGORY).setValue(selectedCategory)
    this.createActivityForm.get(this.TASK_SUB_CATEGORY).setValue(selectedSubCategory)

    this.disableActivityTypeControl('')
    this.createActivityForm.controls[this.nameControlName].enable()

    this.createActivityForm.get(this.nameControlName).markAsUntouched()
    this.createActivityForm.get(this.typeControlName).markAsUntouched()

    this.createActivityForm.get(this.nameControlName).setValue('')
    this.createActivityForm.get(this.typeControlName).setValue('')

    this.activityTaskTypes = []
    this.activityTypes = []
    this.getActivityNameAndType(selectedCategorySelection)
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  ngOnDestroy() {
    this.selectTaskTypesSubscription$?.unsubscribe()
  }
}
